import { GridFilterItem } from '@mui/x-data-grid-pro';

import { PART_FILTER_FIELD_DEFINITIONS } from './partFilterFields';
import { buildFilterModel, filterModelToFilterState } from './partListFilters';
import { AdvancedFilter, FilterState } from '../../../components/molecules/FilterBar';
import {
  parseNumberParam,
  parseSortParam,
  parseStringListParam,
  setOptionalParam,
  setSortParam,
  setStringListParam,
  SortToken,
} from '../../../utils/listUrlState';

/**
 * URL keys the parts-list codec writes. Cleared before re-emit so the URL
 * is the single canonical representation of filter state. Anything outside
 * this list is left untouched.
 */
const OWNED_KEYS = [
  'multiSearchQuery',
  'contract',
  'conditionName',
  'serialised',
  'partTypeName',
  'af',
  'sort',
  'page',
  'pageSize',
] as const;

/** Resolved by useReports on first load and would otherwise re-apply on
 *  every back-button remount on top of the chip state. */
const TRANSIENT_KEYS = ['savedReportCode'] as const;

const MAX_PAGE_SIZE = 200;

const CHIP_KEYS = ['contract', 'conditionName', 'serialised', 'partTypeName', 'af'] as const;

export interface PartListUrlState {
  filterItems: GridFilterItem[];
  hasChipState: boolean;
  multiSearchQuery: string;
  sort: SortToken | null;
  page: number | null;
  pageSize: number | null;
}

export const parsePartListUrl = (searchParams: URLSearchParams): PartListUrlState => {
  const multiSearchQuery = searchParams.get('multiSearchQuery') ?? '';
  const partTypeName = searchParams.get('partTypeName') ?? '';

  const filterState: FilterState = {
    statuses: parseStringListParam(searchParams, 'contract'),
    jobTypes: parseStringListParam(searchParams, 'conditionName'),
    contracts: parseStringListParam(searchParams, 'serialised'),
    contractNames: [],
    extraText: partTypeName,
    jobId: '',
    // Pass multi-search into FilterState so buildFilterModel can split
    // multi-paste tokens into `serial1 isAnyOf` correctly. The hook stores
    // this in its own multiSearchQuery slot for single-token queries; for
    // multi-paste it ends up in filterModel and we surface it back via
    // the multiSearchQuery URL key on writeback.
    multiSearch: multiSearchQuery,
    advancedFilters: parseAdvancedFilters(searchParams),
  };

  const filterItems = buildFilterModel(filterState).items;
  const hasChipState = CHIP_KEYS.some((k) => searchParams.has(k));

  return {
    filterItems,
    hasChipState,
    multiSearchQuery,
    sort: parseSortParam(searchParams),
    page: searchParams.has('page') ? parseNumberParam(searchParams, 'page', 0, { min: 0 }) : null,
    pageSize: searchParams.has('pageSize')
      ? parseNumberParam(searchParams, 'pageSize', 25, { min: 1, max: MAX_PAGE_SIZE })
      : null,
  };
};

const parseAdvancedFilters = (searchParams: URLSearchParams): AdvancedFilter[] => {
  const out: AdvancedFilter[] = [];
  for (const raw of searchParams.getAll('af')) {
    // Each segment is encodeURIComponent-encoded by the writer so values
    // containing the `|` delimiter round-trip safely.
    const parts = raw.split('|').map(decodeAfSegment);
    if (parts.length < 3) continue;
    const [dataKey, operator, value, value2] = parts;
    if (!dataKey || !operator) continue;
    // Drop incomplete `between` entries — without both bounds the chip
    // renders a half-empty range and `buildFilterModel` only emits one
    // half of the date pair. Treat the whole entry as malformed.
    if (operator === 'between' && (value === undefined || !value2)) continue;
    const def = PART_FILTER_FIELD_DEFINITIONS.find((f) => f.key === dataKey);
    const type = def?.type ?? 'text';
    const fieldLabel = def?.name ?? dataKey;
    if (operator === 'between') {
      out.push({
        field: fieldLabel,
        dataKey,
        type,
        operator,
        value,
        value2,
        dateFrom: type === 'date' ? value : undefined,
        dateTo: type === 'date' ? value2 : undefined,
        display: `${fieldLabel} between ${value} and ${value2}`,
      });
    } else {
      out.push({
        field: fieldLabel,
        dataKey,
        type,
        operator,
        value: value ?? '',
        display: `${fieldLabel} ${operator} ${value ?? ''}`,
      });
    }
  }
  return out;
};

const decodeAfSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export interface PartListUrlSnapshot {
  filterModel: { items: GridFilterItem[] };
  multiSearchQuery: string;
  sort: SortToken | null;
  page: number;
  pageSize: number;
}

export const writePartListUrl = (
  params: URLSearchParams,
  snapshot: PartListUrlSnapshot,
  defaultSort: SortToken
): void => {
  for (const key of OWNED_KEYS) params.delete(key);
  for (const key of TRANSIENT_KEYS) params.delete(key);

  // Skip the implicit `isActive=true` baseline emitted by buildFilterModel
  // when no quick filters are set — it's a hidden default the FilterBar
  // doesn't surface as a chip and shouldn't end up in shared URLs.
  const items = snapshot.filterModel.items.filter(
    (i) => !(i.field === 'isActive' && i.operator === 'is' && i.value === 'true')
  );
  const state = filterModelToFilterState({ items });

  setStringListParam(params, 'contract', state.statuses);
  setStringListParam(params, 'conditionName', state.jobTypes);
  setStringListParam(params, 'serialised', state.contracts);
  setOptionalParam(params, 'partTypeName', state.extraText);
  setStringListParam(params, 'af', state.advancedFilters.map(serializeAdvancedFilter));

  // Multi-paste serial inputs are stored as `serial1 isAnyOf […]` on the
  // filter model; filterModelToFilterState surfaces them back as
  // `state.multiSearch = "tok1,tok2,…"`. Prefer that over the hook's
  // single-token multiSearchQuery so both encodings round-trip through URL.
  const multiSearch = state.multiSearch || snapshot.multiSearchQuery;
  setOptionalParam(params, 'multiSearchQuery', multiSearch);

  const isDefaultSort =
    snapshot.sort !== null &&
    snapshot.sort.field === defaultSort.field &&
    snapshot.sort.direction === defaultSort.direction;
  setSortParam(params, isDefaultSort ? null : snapshot.sort);

  if (snapshot.page > 0) params.set('page', String(snapshot.page));
  else params.delete('page');

  if (snapshot.pageSize !== 25) params.set('pageSize', String(snapshot.pageSize));
  else params.delete('pageSize');
};

const serializeAdvancedFilter = (adv: AdvancedFilter): string => {
  if (adv.operator === 'between') {
    const fromValue = adv.dateFrom ?? adv.value ?? '';
    const toValue = adv.dateTo ?? adv.value2 ?? '';
    return [adv.dataKey, 'between', fromValue, toValue].map(encodeURIComponent).join('|');
  }
  return [adv.dataKey, adv.operator, adv.value ?? ''].map(encodeURIComponent).join('|');
};
